import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Sparkles, Heart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function PraiseWall() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  const load = () => {
    api.get("/prayers/wall").then((r) => setItems(r.data || [])).catch(() => {});
  };

  const handlePray = async (id) => {
    try {
      await api.post(`/prayers/${id}/pray`);
      load();
    } catch {}
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <section id="praise" data-testid="praise-wall-section" className="py-24 lg:py-32 bg-[#F3EFEA] min-h-[400px]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="mb-12 max-w-3xl">
          <div className="text-xs uppercase tracking-[0.2em] text-[#5C6661] mb-4">He hears &amp; He answers</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#1C201E] leading-tight">
            Items of Praise
          </h2>
          <p className="mt-4 text-[#5C6661]">Testimonies from our church family, shared with their consent.</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.length === 0 ? (
            <div className="col-span-full text-center py-20 text-[#5C6661] italic bg-white/30 rounded-3xl border border-dashed border-[#E5E0D8]">
              The community wall is currently empty. Check back soon for testimonies and prayer.
            </div>
          ) : items.map((p, i) => {
            const at = p.created_at ? new Date(p.created_at).toLocaleDateString(undefined, { month: "short", year: "numeric" }) : "";
            return (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                data-testid={`praise-card-${i}`}
                className="p-8 bg-[#FAF9F6] border border-[#E5E0D8] rounded-2xl relative overflow-hidden"
              >
                <Sparkles className="w-4 h-4 text-[#D4A373] mb-4" />
                <blockquote className="font-serif-display text-xl text-[#1C201E] leading-snug">
                  &ldquo;{p.request}&rdquo;
                </blockquote>
                <div className="mt-5 pt-5 border-t border-[#E5E0D8] flex items-center justify-between">
                  <div className="text-sm text-[#1C201E]">— {p.name || "A church member"}</div>
                  <div className="flex items-center gap-3">
                    {user && (
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handlePray(p.id)}
                          className="flex items-center gap-1.5 text-[10px] uppercase font-bold text-red-500 hover:scale-105 active:scale-95 transition-transform bg-red-50 px-2.5 py-1 rounded-full border border-red-100"
                          title="I Prayed for This"
                        >
                          <Heart className="w-3.5 h-3.5 fill-current" />
                          <span>{p.prayed_for_count || 0} {p.kind === 'praise' ? 'Amen!' : 'prayed'}</span>
                        </button>
                      </div>
                    )}
                    {at && <div className="text-xs uppercase tracking-[0.15em] text-[#5C6661]">{at}</div>}
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
